import { agreementSigned, agreementSignedFor, defaultAgreement, unsigned, withSignedService, type V2Agreement } from './agreement';
import { newId } from './ids';
import type { AreaKind } from './areaCatalog';
import { inspectionServiceOf, type InspectionService } from './inspectionService';
import { normalizeJurisdictionCode, type JurisdictionCode } from './jurisdiction';
import { DESCRIPTOR_CATALOGUE, type DescriptorAnswer, type DescriptorKey, type HeatingFuel, type UtilitySource } from './descriptors';
import type { InspectionScopeSnapshot } from './scopeGate';
import type { AlbumSaveState, EvidenceSource } from './evidence';
import type { DefectSeverity } from './severity';

export type { AreaKind } from './areaCatalog';
export { AREA_KIND_LABELS } from './areaCatalog';
export { inspectionServiceOf, isInspectionService, INSPECTION_SERVICE_ORDER, type InspectionService } from './inspectionService';

// TRUE V2 domain model — versioned from day one. Records carry schemaVersion explicitly so
// migrations are declared (src/persistence/schema.ts), never inferred from field shapes.
// Version 2 (Phase 1): full field model — property/client/inspector, first-class areas with
// enable/note/completion, checklist items with hint/reason/evidence links.
// Later rounds only ADD optional fields (Report Language, floors, reason codes, the agreement, the
// inspection date, the start mode, basement details, the finalization lock); records written before a
// field existed read back with the field's default (schema.ts) and the schemaVersion stays 2.
export const V2_SCHEMA_VERSION = 2;

// Disposition belongs to the inspector, never to AI (OWNER-CONTRACT §4: evidence coverage and
// inspector disposition are two independent dimensions; this enum is the inspector-owned one).
// Five states, named per the 2026-09-01 Phase 1 task book §7. "notApplicable" and
// "unableToInspect" carry a free-text reason (Frozen Spec §9: 无此项 / 不适用 / 无法接近 /
// 无法观察 / 其他) so no expected item ever disappears silently.
export type ItemStatus = 'notChecked' | 'checkedOk' | 'issueFound' | 'notApplicable' | 'unableToInspect';

// Unable to Inspect reasons (Owner task book 2026-09-04 §15 quick reason sheet; Final Authority §11):
// a closed enumeration persisted as `reasonCode`; `statusReason` keeps the inspector's OPTIONAL
// free-text note. A reason is required; the note is not. Added without a schemaVersion bump —
// records written before the field exists read back as null (src/persistence/schema.ts).
export type UnableReason = 'inaccessible' | 'obstructed' | 'utilitiesOff' | 'unsafeCondition' | 'weather' | 'notInstalled' | 'other';

export const UNABLE_REASON_ORDER: readonly UnableReason[] = Object.freeze([
  'inaccessible',
  'obstructed',
  'utilitiesOff',
  'unsafeCondition',
  'weather',
  'notInstalled',
  'other'
]);

export function isUnableReason(value: unknown): value is UnableReason {
  return typeof value === 'string' && (UNABLE_REASON_ORDER as readonly string[]).includes(value);
}

// NY F1 Round 3 (Owner decision OD-5 = A, 2026-09-06): a portion of a component that could not be
// observed — a limitation record attached to the EXISTING row. The row keeps the inspector result of
// the portion actually observed; whole-row unableToInspect stays reserved for the case where nothing of
// the component was observed (NY-INSPECTION-LIMITATIONS.md §8; PP-22). Recording a portion never adds
// a row, never creates a task and never turns "not observed" into "not present". A portion is present
// but not observed, so `notInstalled` is not a portion reason.
export interface LimitedPortion {
  reasonCode: UnableReason;
  // The portion in the inspector's words ('' = not named; the report then says "a part").
  extent: string;
  // The inspector's factual wording — REQUIRED for `other` (OD-7 = C); optional otherwise. For
  // `obstructed` the frozen template (src/domain/authority/ny/limitationTemplates.ts) carries the report
  // reason while this is blank.
  note: string;
  // The frozen template chosen when the portion was recorded (null = none applies).
  templateId: string | null;
}

export const PORTION_REASON_ORDER: readonly UnableReason[] = Object.freeze(UNABLE_REASON_ORDER.filter((reason) => reason !== 'notInstalled'));

export function isPortionReason(value: unknown): value is UnableReason {
  return isUnableReason(value) && value !== 'notInstalled';
}

// OD-7 = C: only `other` needs the inspector's own words as the reason; every other closed code carries
// its reason in the label (or, for `obstructed`, in the frozen template). Blank / whitespace = missing.
export function unableReasonNeedsNote(reasonCode: UnableReason | null): boolean {
  return reasonCode === 'other';
}

export function limitedPortionIncomplete(portion: Pick<LimitedPortion, 'reasonCode' | 'note'>): boolean {
  return unableReasonNeedsNote(portion.reasonCode) && portion.note.trim().length === 0;
}

export type InspectionStatus = 'draft' | 'inProgress' | 'completed';

export type PropertyType = 'singleFamily' | 'multiFamily' | 'condo' | 'townhouse' | 'other';

// CK-10: the statutory definition a New York home inspection is written for (RPL §444-b(6)).
export const MAX_SUPPORTED_DWELLING_UNITS = 4;

// A recorded unit count: a whole number of dwellings, at least one. Anything else is "not asked".
export function isUnitCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 999;
}

// Basement: space TYPE and finish STATE are two separate facts (Owner task book §7).
export type BasementType = 'full' | 'partial' | 'crawlspace' | 'slab' | 'none';
export type BasementFinish = 'unfinished' | 'finished' | 'partial' | 'mixed' | 'unknown';

export const BASEMENT_TYPE_ORDER: readonly BasementType[] = Object.freeze(['full', 'partial', 'crawlspace', 'slab', 'none']);
export const BASEMENT_FINISH_ORDER: readonly BasementFinish[] = Object.freeze(['unfinished', 'finished', 'partial', 'mixed', 'unknown']);

export function isBasementType(value: unknown): value is BasementType {
  return typeof value === 'string' && (BASEMENT_TYPE_ORDER as readonly string[]).includes(value);
}

export function isBasementFinish(value: unknown): value is BasementFinish {
  return typeof value === 'string' && (BASEMENT_FINISH_ORDER as readonly string[]).includes(value);
}

// How the inspection was started (Owner task book §8): importing existing photos into the photo
// pool first, or inspecting live on site. null = not chosen yet (records written before the field).
export type StartMode = 'live' | 'import';

export function isStartMode(value: unknown): value is StartMode {
  return value === 'live' || value === 'import';
}

// Report Language is INSPECTION DATA — the language the client report is produced in — and is a
// different field from the device's App Language (OWNER-CONTRACT §9.1 / Final Authority §21.2).
// It defaults to English, is chosen per inspection by the inspector, and is never derived from or
// changed by the App Language. The product now offers English or Chinese (Owner task book §18:
// Chinese = English client PDF + Chinese self copy; there is no bilingual single PDF). `bilingual`
// stays readable for records written by Batch 1 and is treated as English on output.
export type ReportLanguage = 'en-US' | 'zh-CN' | 'bilingual';

export const REPORT_LANGUAGE_ORDER: readonly ReportLanguage[] = Object.freeze(['en-US', 'zh-CN', 'bilingual']);

export const DEFAULT_REPORT_LANGUAGE: ReportLanguage = 'en-US';

export function isReportLanguage(value: unknown): value is ReportLanguage {
  return typeof value === 'string' && (REPORT_LANGUAGE_ORDER as readonly string[]).includes(value);
}

export interface V2Property {
  address: string;
  city: string;
  state: string;
  zip: string;
  // NY F1 Round 1 (RT-03 / RT-04). The `state` string above stays exactly as written — its
  // AUTHORITY is carried here. Before this round `state` was seeded by a form default
  // (useState('NY')), which is not an inspector assertion; `stateConfirmed` is true only once the
  // inspector actually chose the state. Records written before the field read back false.
  stateConfirmed: boolean;
  // CK-11 / PP-20: was this property previously occupied as a dwelling? null = UNKNOWN, never
  // inferred from year built, address, photos, appearance, checklist content or AI — the statute's
  // own question, asked of the inspector and nothing more. Records written before the field read
  // back null and are never rewritten.
  previouslyOccupied: boolean | null;
  // null = not set yet (Round 2 §D: a new inspection never pretends to know the property type).
  propertyType: PropertyType | null;
  // CK-10 (Owner ruling OD-6, 2026-09-09): how many dwelling units the building has. New York
  // defines a home inspection for one to four units (§444-b(6)); more than four is blocked with a
  // notice (src/domain/inspectionContext.ts). null = not asked — never inferred from the property
  // type, and never a block on its own. A "5 or more" answer is recorded as 5.
  unitCount: number | null;
  yearBuilt: number | null;
  basementType: BasementType | null;
  basementFinish: BasementFinish | null;
  // NY F1 Round 2 (CK-01 · 197-5.8(d) · 197-5.10(a)(1); OD-1 = A1): the three mandatory descriptors
  // that belong to the property rather than to one checklist row, asked once at setup. null = never
  // asked — NOT the same as a chosen 'unknown' (asked, undetermined; a lawful printed value for the
  // two utility sources, never for the fuel). Records written before the field read back null and are
  // never rewritten (frozen plan §6).
  waterSupplySource: UtilitySource | null;
  wasteDisposalSource: UtilitySource | null;
  heatingFuel: HeatingFuel | null;
}

export interface V2Client {
  name: string;
  phone: string;
  email: string;
}

// Phase 1 carries one licence status. The enum exists so it can widen without a data migration;
// the value is the inspector's own assertion, recorded as given and NEVER verified against any
// registry by this product (RP-01 / RT-04; licence-authenticity lookup is out of scope).
export type LicenseStatus = 'licensed';

export function isLicenseStatus(value: unknown): value is LicenseStatus {
  return value === 'licensed';
}

// The inspector as recorded ON THIS INSPECTION. Snapshotted at creation from the device profile
// (src/state/preferences.ts) so editing the profile later never rewrites a delivered report.
export interface V2InspectorRef {
  name: string;
  // The state the licence is held in; null = unknown. Never silently 'NY'.
  licensedState: JurisdictionCode | null;
  // '' = not entered. Never invented.
  licenseNumber: string;
  licenseStatus: LicenseStatus | null;
}

export function emptyInspectorRef(): V2InspectorRef {
  return { name: '', licensedState: null, licenseNumber: '', licenseStatus: null };
}

// How this inspection came into existence. Written ONCE at creation and never by any production
// UI action afterwards, so the classification cannot be flipped to reach a blocked capability
// (PP-21). null on records written before the field: they read back as a real client engagement,
// which is the fail-closed side.
//   realClient    an ordinary client engagement — the default for anything else
//   demo          created as a Demo inspection (演示验屋): no fee, every output DEMO-watermarked
//   internalTest  created by an internal seam only: development scenarios, tests, benchmarks
export type InspectionContextOrigin = 'realClient' | 'demo' | 'internalTest';

export const INSPECTION_CONTEXT_ORIGIN_ORDER: readonly InspectionContextOrigin[] = Object.freeze(['realClient', 'demo', 'internalTest']);

export function isInspectionContextOrigin(value: unknown): value is InspectionContextOrigin {
  return typeof value === 'string' && (INSPECTION_CONTEXT_ORIGIN_ORDER as readonly string[]).includes(value);
}

// The origin a NEW inspection is created with. createInspection() uses it, and the creation form
// asks the same function, so the form's gate and the stored record can never disagree.
export function draftContextOrigin(agreement: { demo: boolean }): InspectionContextOrigin {
  return agreement.demo ? 'demo' : 'realClient';
}

export interface V2Inspection {
  schemaVersion: number;
  id: string;
  property: V2Property;
  client: V2Client;
  inspector: V2InspectorRef;
  status: InspectionStatus;
  weather: string;
  notes: string;
  // Language of the client report for THIS inspection (§21.2). Independent of the App Language.
  reportLanguage: ReportLanguage;
  // Owner decision list 2026-10-19 item 23: also produce a short Chinese summary PDF for the client
  // (its own file, saying the English report governs). Off unless the inspector switches it on.
  // Records written before the field existed read back false.
  clientChineseSummary: boolean;
  // Scheduled / actual inspection date-time (Owner task book §5). Defaults to the creation time.
  inspectionAt: number;
  startMode: StartMode | null;
  agreement: V2Agreement;
  // NY F1 Round 1 (PP-21): frozen at creation, never rewritten. See InspectionContextOrigin.
  contextOrigin: InspectionContextOrigin | null;
  // NY F1 Round 4 (plan §5.4; runtime contract §6): the New York scope dispositions as they stood
  // when THIS agreement was signed. Taken at signing, never back-filled onto an inspection that was
  // signed before this round (that would forge provenance, plan §6), and cleared when the document
  // is re-opened. null = no snapshot, which the Scope Gate reads as "no New York packet" and fails
  // closed on. Records written before the field existed read back null.
  scopeSnapshot: InspectionScopeSnapshot | null;
  // Set when the final report was generated: the inspection is locked from then on (§18).
  finalizedAt: number | null;
  // NY F1 Round 5 (OD-12): set when the working data was deleted by a final report generation.
  // null = nothing was purged (every inspection before the ruling, and every open inspection).
  purge: InspectionPurge | null;
  // Owner device feedback 2026-09-14: the report cover photo (bytes under coverMediaKey(id) in the media
  // store). null = none was taken. Records written before the field existed read back null.
  coverPhoto: InspectionCoverPhoto | null;
  // Owner 2026-10-20 (an unfinished inspection lives on ONE device): this copy was exported to a file to be
  // carried to another device, and is read-only here from then on. null = it is worked on here. Records
  // written before the field existed read back null.
  movedOut: InspectionMove | null;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
}

export interface InspectionMove {
  at: number;
  // The name of the file it went into, so the inspector can find it again.
  fileName: string;
}

export function isInspectionMovedOut(inspection: Pick<V2Inspection, 'movedOut'>): boolean {
  return inspection.movedOut !== null;
}

// Work on this inspection may continue on THIS device: no final report, and not carried away by a file.
export function isInspectionWorkable(inspection: Pick<V2Inspection, 'finalizedAt' | 'movedOut'>): boolean {
  return inspection.finalizedAt === null && inspection.movedOut === null;
}

export function withMovedOut(inspection: V2Inspection, move: InspectionMove | null, now: number): V2Inspection {
  return { ...inspection, movedOut: move, updatedAt: now };
}

// The one basic-information row an inspection leaves behind after OD-12 deleted its working data
// (approved draft §5.3: "地址 · 日期 · 客户 · PDF 数 · 已出报告"). The address, the inspection date
// and the client name stay on the inspection record itself; the counts are frozen here so every
// list, count and progress line in the app keeps a truthful number without the records that
// produced it. Nothing here can reconstruct a finding, a photo or a result — they are counts.
export interface InspectionPurge {
  purgedAt: number;
  // The generation run that triggered the purge (= V2Inspection.finalizedAt).
  generatedAt: number;
  // Final report PDFs + the signed agreement PDF kept for this inspection.
  pdfCount: number;
  service: InspectionService;
  // Frozen at generation: the checklist counts, the area completion and the number of confirmed
  // findings the report was built from.
  progress: ProgressSummary;
  areas: AreaCompletionSummary;
  findingCount: number;
}

export function isInspectionPurged(inspection: Pick<V2Inspection, 'purge'>): boolean {
  return inspection.purge !== null;
}

// The report cover photo (Owner device feedback 2026-09-14): ONE photo of the house, taken or chosen when
// the inspection is set up or from the inspection card later. Shown on the inspection card, as the
// thumbnail of the Reports list and printed on the report cover. It is NOT evidence: it never enters the
// photo pool, is never linked to a row or a finding and is never sent to the AI. The bytes live in the
// media store under coverMediaKey(inspectionId); this record carries the facts about them. It survives
// the OD-12 purge (the delivered report shows it) and goes with the inspection when that is deleted.
export interface InspectionCoverPhoto {
  source: EvidenceSource;
  mimeType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  // Album ledger of a camera capture (OC §6.1); a library pick is never re-saved → notRequired.
  albumSaveState: AlbumSaveState;
  albumSaveDetail: string;
  // Bumped on every replacement (cached object URLs are keyed by the media key, not the bytes).
  version: number;
  updatedAt: number;
}

export function coverMediaKey(inspectionId: string): string {
  return `cover:${inspectionId}`;
}

export function withCoverPhoto(inspection: V2Inspection, coverPhoto: InspectionCoverPhoto | null, now: number): V2Inspection {
  return touchedInspection({ ...inspection, coverPhoto }, now);
}

// Language-neutral descriptor of a plan-generated area name so the name can be re-expressed in the
// App Language or, independently, the Report Language (task book §3 / §18: the English client PDF of
// a zh-CN inspection prints "Second Floor Bedroom 1", not "二楼卧室 1"). null = inspector-named
// (custom or renamed): the stored name is printed as is. Resolved by src/i18n/labels.ts.
export interface AreaNameLabel {
  // RoomKey | SpaceKey | SystemKey of src/domain/housePlan.ts.
  preset: string;
  // 1-based instance number when several instances share the preset; null for a single one.
  index: number | null;
  // Floor prefix printed (the house has more than one floor).
  withFloor: boolean;
}

export interface V2Area {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  kind: AreaKind;
  name: string;
  // Optional on disk: absent on records written before the Owner Test build → null.
  label: AreaNameLabel | null;
  // Disabled areas stay in the record (nothing is deleted) but leave progress and completion.
  enabled: boolean;
  // Floor this area is on (1 = 一楼), or null for areas that are not tied to a floor (exterior,
  // roof, garage, basement, attic, systems). Optional on disk: records written before floors
  // existed (Build 81) read back as null and are never rewritten (src/persistence/schema.ts).
  floor: number | null;
  // Area Scan "MANUAL / SKIP AI" state (Final Implementation Authority §7.2), persisted per area.
  // Optional on disk: records written before the field exists read back as false.
  aiSkipped: boolean;
  // Area overall note for the AI scan (区域整体备注, Round 2 §H): what the inspector wants looked at.
  // Persisted whether or not AI ever runs; optional on disk → ''.
  scanNote: string;
  note: string;
  // 2026-10-19 (Owner item 31): the photo-checklist shots the inspector said do not apply / are not here
  // (src/domain/photoGuide.ts). Optional on disk → [].
  shotSkips?: string[];
  // null = not completed. Completion is reversible: reopening simply clears this.
  completedAt: number | null;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

// 2026-09-14 (Owner: "报告语由 AI 写"): the report sentence written for this row — by the AI from the
// inspector's comment, photo notes, reviewed AI observation and findings, or typed by the inspector.
// `basis` fingerprints the facts it was written from; when they change, the sentence is stale and the
// report falls back to the library sentence until it is written again.
export interface ItemReportSentence {
  en: string;
  zh: string;
  source: 'ai' | 'inspector';
  basis: string;
  at: number;
  // Owner device test 2026-09-21: the inspector may now write the sentence in CHINESE (the App Language
  // of most inspectors) as well as in English. `edited` is the language the inspector wrote LAST; the
  // other language still holds what stood before and is "not synced" until the AI renders the
  // inspector's words again (src/state/aiStore.ts, fromInspectorWording) or the inspector edits it too.
  // Only meaningful with source 'inspector'; absent on records written before the field existed = 'en'
  // (English was the only editable side then).
  edited?: 'en' | 'zh';
}

// The language an inspector-authored sentence was last written in (see ItemReportSentence.edited).
export function reportSentenceEditedLanguage(sentence: ItemReportSentence): 'en' | 'zh' | null {
  if (sentence.source !== 'inspector') return null;
  return sentence.edited === 'zh' ? 'zh' : 'en';
}

export interface V2ChecklistItem {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  areaId: string;
  // Template row id from src/domain/checklistTemplates.ts, or null for inspector-added rows.
  templateId: string | null;
  title: string;
  hint: string;
  status: ItemStatus;
  // The inspector's own working note — deliberately separate from any Finding (task book §16).
  inspectorNote: string;
  // Reason for notApplicable / unableToInspect; cleared when the status leaves those states.
  // For Unable to Inspect this is the inspector's optional note (task book §15); for N/A it is
  // the optional note (§12).
  statusReason: string;
  // Unable to Inspect reason (closed enumeration, §15). Only meaningful with unableToInspect.
  reasonCode: UnableReason | null;
  // NY F1 Round 2 (OD-1 = A1): the inspector's typed answers to the mandatory descriptors this row
  // carries (src/domain/descriptors.ts). [] on every row written before the field existed and on every
  // row that carries no descriptor; an inspector note is NEVER parsed into one (frozen plan §6).
  descriptors: DescriptorAnswer[];
  // NY F1 Round 3 (OD-5 = A): the portions of this component that could not be observed, each a
  // printed limitation with its reason. [] on every row written before the field existed. Only a row
  // that can carry an observed result keeps them (cleared by whole-row Unable and by N/A).
  limitedPortions: LimitedPortion[];
  // Evidence links (OWNER-CONTRACT §6.2 "evidence links" layer). Capture location lives on the
  // evidence record itself and is never rewritten by linking.
  evidenceIds: string[];
  // Final Owner Decision 2026-09-14 (CA-7 / D-CA-5): photos the inspector REMOVED from this row. The
  // removal is an inspector override — an AI run (area scan, advanced review), a reload, a sync or a
  // report generation never links such a photo to this row again. Only the inspector, by linking it
  // back deliberately, clears the override.
  evidenceExclusions: string[];
  // 2026-10-27 (Owner, device walkthrough: 「AI 分拣默认采信」): photos the AI PLACED on this row — the area
  // scan's photoIds, the pool sort's placements, an unclassified observation filed here. Since this date
  // they are LINKED at once (also in evidenceIds, so they are evidence and print in the report) and this
  // list only marks them 「AI 放的」 until the inspector touches them: linking the photo by hand or confirming
  // clears the mark, 「不是这项」 unlinks it and records the removal override (evidenceExclusions). An
  // excluded photo is never placed again. (2026-10-18 to 2026-10-26 the same list was a proposal that
  // waited for a tap per photo — 164 taps on one area in the walkthrough; schema.ts migrates such rows.)
  evidenceSuggestions: string[];
  reportSentence: ItemReportSentence | null;
  // 2026-09-13 (TW-3): the inspector recorded an operational test on this row (smoke / CO alarm test
  // button; since 2026-09-29 also the GFCI / receptacle test and the window, door, garage door and
  // bathroom fan operation). false = observed only — the report never says it was tested or operated.
  operationalTestPerformed: boolean;
  // 2026-09-13 ratification §4 (REFERRAL = ROW + INSPECTOR-CONFIRMED FACT + AUTHORITY), carried on the
  // row since 2026-09-14 (CA-15 removed the row-level finding it used to live on): the inspector
  // confirmed movement / displacement / significant cracking / deformation / suspected instability /
  // a load-bearing concern → the referral for this row is a Professional Engineer (PE).
  structuralConcern: boolean;
  // 2026-10-19 (Owner decision list item 16): the defect tier the INSPECTOR chose for this row
  // (src/domain/severity.ts). null = not chosen; the report then prints the suggested tier. Only
  // meaningful while the row is a defect.
  severity: DefectSeverity | null;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

export const ITEM_STATUS_ORDER: readonly ItemStatus[] = Object.freeze([
  'notChecked',
  'checkedOk',
  'issueFound',
  'notApplicable',
  'unableToInspect'
]);

export const REASON_STATUSES: readonly ItemStatus[] = Object.freeze(['notApplicable', 'unableToInspect']);

export const PROPERTY_TYPE_ORDER: readonly PropertyType[] = Object.freeze([
  'singleFamily',
  'multiFamily',
  'townhouse',
  'condo',
  'other'
]);

export function isItemStatus(value: unknown): value is ItemStatus {
  return typeof value === 'string' && (ITEM_STATUS_ORDER as readonly string[]).includes(value);
}

export function statusNeedsReason(status: ItemStatus): boolean {
  return REASON_STATUSES.includes(status);
}

// ---------- factories ----------

export interface NewInspectionInput {
  property: Partial<V2Property> & { address: string };
  client?: Partial<V2Client>;
  inspector?: Partial<V2InspectorRef>;
  weather?: string;
  notes?: string;
  reportLanguage?: ReportLanguage;
  inspectionAt?: number;
  startMode?: StartMode | null;
  agreement?: V2Agreement;
  // Only an internal seam (development scenarios, tests, benchmarks) passes this. The production
  // New Inspection form never does: it leaves the origin to be derived from the agreement below.
  contextOrigin?: InspectionContextOrigin;
}

function clean(value: string | undefined): string {
  return (value ?? '').trim();
}

export function createInspection(input: NewInspectionInput, now: number): V2Inspection {
  const previouslyOccupied = input.property.previouslyOccupied ?? null;
  // OD-9: an agreement created already signed (the New Inspection form signs in place) records the
  // service it was signed for, derived from the answer the inspector just gave.
  const agreement = withSignedService(input.agreement ?? defaultAgreement(), inspectionServiceOf({ previouslyOccupied }));
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('insp'),
    property: {
      address: clean(input.property.address),
      city: clean(input.property.city),
      state: clean(input.property.state),
      zip: clean(input.property.zip),
      stateConfirmed: input.property.stateConfirmed ?? false,
      previouslyOccupied,
      propertyType: input.property.propertyType ?? null,
      unitCount: isUnitCount(input.property.unitCount) ? input.property.unitCount : null,
      yearBuilt: input.property.yearBuilt ?? null,
      basementType: input.property.basementType ?? null,
      basementFinish: input.property.basementFinish ?? null,
      waterSupplySource: input.property.waterSupplySource ?? null,
      wasteDisposalSource: input.property.wasteDisposalSource ?? null,
      heatingFuel: input.property.heatingFuel ?? null
    },
    // B5 (2026-10-27): an agreement signed in the New Inspection form brings the name the client signed under.
    client: withSignedClientName(
      {
        name: clean(input.client?.name),
        phone: clean(input.client?.phone),
        email: clean(input.client?.email)
      },
      agreement
    ),
    inspector: {
      name: clean(input.inspector?.name),
      licensedState: input.inspector?.licensedState ?? null,
      licenseNumber: clean(input.inspector?.licenseNumber),
      licenseStatus: input.inspector?.licenseStatus ?? null
    },
    status: 'draft',
    weather: clean(input.weather),
    notes: clean(input.notes),
    reportLanguage: input.reportLanguage ?? DEFAULT_REPORT_LANGUAGE,
    clientChineseSummary: false,
    inspectionAt: input.inspectionAt ?? now,
    startMode: input.startMode ?? null,
    agreement,
    // Frozen here and nowhere else. An inspection created as a Demo is a Demo for life; every
    // other production creation is a real client engagement and can never be relabelled.
    contextOrigin: input.contextOrigin ?? draftContextOrigin(agreement),
    // Round 4: a snapshot belongs to a SIGNING, so creation never writes one. The store takes it
    // through withScopeSnapshot() the moment the agreement reaches the signed state.
    scopeSnapshot: null,
    finalizedAt: null,
    purge: null,
    coverPhoto: null,
    movedOut: null,
    createdAt: now,
    updatedAt: now,
    completedAt: null
  };
}

// The inspector's one-time answers to the NY jurisdiction / PP-20 questions (Round 1, OC-1).
// Every field is optional: only what the inspector actually answered is written, and an answer is
// never invented. Choosing a state is what turns the stored string into an asserted fact.
export interface PropertyJurisdictionPatch {
  state?: string;
  previouslyOccupied?: boolean | null;
  // CK-10: the dwelling-unit count. An unusable value clears the answer, it never guesses one.
  unitCount?: number | null;
}

export function withPropertyJurisdiction(inspection: V2Inspection, patch: PropertyJurisdictionPatch, now: number): V2Inspection {
  const property = { ...inspection.property };
  if (patch.state !== undefined) {
    const code = normalizeJurisdictionCode(patch.state);
    // An unrecognised string is stored as typed but never counts as an assertion.
    property.state = code ?? patch.state.trim();
    property.stateConfirmed = code !== null;
  }
  if (patch.previouslyOccupied !== undefined) property.previouslyOccupied = patch.previouslyOccupied;
  if (patch.unitCount !== undefined) property.unitCount = isUnitCount(patch.unitCount) ? patch.unitCount : null;
  return alignAgreementToService({ ...inspection, property, updatedAt: now });
}

// OD-9: the answer to "previously occupied?" decides WHICH document the client signs. Changing it
// after signing therefore re-opens the agreement — the signatures on a home-inspection agreement
// are not signatures on a New Build observation agreement, and vice versa. Nothing else moves.
export function alignAgreementToService(inspection: V2Inspection): V2Inspection {
  const service = inspectionServiceOf(inspection.property);
  if (!agreementSigned(inspection.agreement)) return inspection;
  if (agreementSignedFor(inspection.agreement, service)) return inspection;
  return { ...inspection, agreement: unsigned(inspection.agreement) };
}

// The property's state as an authority-bearing code: only once the inspector asserted it.
export function assertedPropertyState(property: Pick<V2Property, 'state' | 'stateConfirmed'>): JurisdictionCode | null {
  return property.stateConfirmed ? normalizeJurisdictionCode(property.state) : null;
}

// The inspector chooses the Report Language per inspection (Report screen).
export function withReportLanguage(inspection: V2Inspection, reportLanguage: ReportLanguage, now: number): V2Inspection {
  if (inspection.reportLanguage === reportLanguage) return inspection;
  return { ...inspection, reportLanguage, updatedAt: now };
}

// The inspector switches the client's Chinese summary on or off per inspection (Report screen).
export function withClientChineseSummary(inspection: V2Inspection, on: boolean, now: number): V2Inspection {
  if (inspection.clientChineseSummary === on) return inspection;
  return { ...inspection, clientChineseSummary: on, updatedAt: now };
}

// Saving the agreement also records WHICH document it was signed for (OD-9), from the property fact
// the inspector answered — never from a separate, editable field.
// Device walkthrough 2026-10-27 B5 (10-26 #4): the client's name is often typed only in the signing sheet, and
// the list, the overview and the report then said 「未填客户」 for a client who had signed. The name the
// client signed under fills the inspection's client name when that is still empty — a name the inspector
// typed is never overwritten.
export function withAgreement(inspection: V2Inspection, agreement: V2Agreement, now: number): V2Inspection {
  const stamped = withSignedService(agreement, inspectionServiceOf(inspection.property));
  return { ...inspection, client: withSignedClientName(inspection.client, stamped), agreement: stamped, updatedAt: now };
}

function withSignedClientName(client: V2Client, agreement: V2Agreement): V2Client {
  if (client.name.trim().length > 0 || !agreementSigned(agreement)) return client;
  const signed = agreement.client.name.trim();
  return signed.length > 0 ? { ...client, name: signed } : client;
}

// The client's name as every screen shows it: the inspection's own, else the name typed in the agreement
// (records signed before 2026-10-27 kept it only there). '' when neither has one.
export function clientNameOf(inspection: Pick<V2Inspection, 'client' | 'agreement'>): string {
  const own = inspection.client.name.trim();
  return own.length > 0 ? own : inspection.agreement.client.name.trim();
}

// NY F1 Round 4: the scope snapshot of the signed agreement, or null while the document is open.
// The value itself is derived by src/domain/scopeGate.ts; this function only stores it, so the
// inspection record keeps no knowledge of New York scope rules.
export function withScopeSnapshot(inspection: V2Inspection, scopeSnapshot: InspectionScopeSnapshot | null, now: number): V2Inspection {
  if (inspection.scopeSnapshot === null && scopeSnapshot === null) return inspection;
  return { ...inspection, scopeSnapshot, updatedAt: now };
}

export function withStartMode(inspection: V2Inspection, startMode: StartMode | null, now: number): V2Inspection {
  if (inspection.startMode === startMode) return inspection;
  return { ...inspection, startMode, updatedAt: now };
}

// The final report was generated: from now on the working inspection data is read-only (§18).
export function finalizedInspection(inspection: V2Inspection, now: number): V2Inspection {
  return { ...inspection, status: 'completed', completedAt: inspection.completedAt ?? now, finalizedAt: now, updatedAt: now };
}

export function isInspectionLocked(inspection: Pick<V2Inspection, 'finalizedAt'>): boolean {
  return inspection.finalizedAt !== null;
}

// OD-12 (Owner ruling 2026-09-09): the record an inspection keeps after its final report deleted the
// working data. What survives is exactly what the approved draft §5.3 lists — the address, the
// inspection date, the client's name, the PDF count and the generation date — plus the frozen counts
// the app's lists need. Everything else on the record is emptied HERE, in one place: the client's
// phone and e-mail, the inspector snapshot, the weather, the inspection notes, the property facts,
// the fee and both hand-drawn signatures (the signed Agreement PDF keeps them).
// Nothing outside this database is touched — the device's own photo album is never involved.
export function purgedInspection(inspection: V2Inspection, purge: InspectionPurge, now: number): V2Inspection {
  const p = inspection.property;
  return {
    ...inspection,
    property: {
      address: p.address,
      city: p.city,
      state: p.state,
      zip: p.zip,
      stateConfirmed: p.stateConfirmed,
      // The service fact stays: it says which document the kept PDFs are.
      previouslyOccupied: p.previouslyOccupied,
      propertyType: null,
      unitCount: null,
      yearBuilt: null,
      basementType: null,
      basementFinish: null,
      waterSupplySource: null,
      wasteDisposalSource: null,
      heatingFuel: null
    },
    // The client's name survives (§5.3) — the agreement's when the record itself never had one (B5).
    client: { name: clientNameOf(inspection), phone: '', email: '' },
    inspector: emptyInspectorRef(),
    weather: '',
    notes: '',
    startMode: null,
    agreement: {
      ...defaultAgreement(),
      documentVersion: inspection.agreement.documentVersion,
      documentLanguage: inspection.agreement.documentLanguage,
      demo: inspection.agreement.demo,
      clientAiConsent: inspection.agreement.clientAiConsent,
      clientAiConsentAt: inspection.agreement.clientAiConsentAt,
      aiConsentWithdrawnAt: inspection.agreement.aiConsentWithdrawnAt,
      clientEsignConsent: inspection.agreement.clientEsignConsent,
      clientEsignConsentAt: inspection.agreement.clientEsignConsentAt,
      clientLiabilityAck: inspection.agreement.clientLiabilityAck,
      clientLiabilityAckAt: inspection.agreement.clientLiabilityAckAt,
      additionalServices: inspection.agreement.additionalServices,
      signedVersion: inspection.agreement.signedVersion,
      signedService: inspection.agreement.signedService,
      signedAt: inspection.agreement.signedAt
    },
    purge,
    updatedAt: now
  };
}

export function createArea(
  inspectionId: string,
  kind: AreaKind,
  name: string,
  sortOrder: number,
  now: number,
  floor: number | null = null,
  label: AreaNameLabel | null = null
): V2Area {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('area'),
    inspectionId,
    kind,
    name: name.trim(),
    enabled: true,
    floor,
    label,
    aiSkipped: false,
    scanNote: '',
    note: '',
    completedAt: null,
    sortOrder,
    createdAt: now,
    updatedAt: now
  };
}

export interface NewItemInput {
  templateId: string | null;
  title: string;
  hint?: string;
}

export function createItem(
  inspectionId: string,
  areaId: string,
  input: NewItemInput,
  sortOrder: number,
  now: number
): V2ChecklistItem {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('item'),
    inspectionId,
    areaId,
    templateId: input.templateId,
    title: input.title.trim(),
    hint: (input.hint ?? '').trim(),
    status: 'notChecked',
    inspectorNote: '',
    statusReason: '',
    reasonCode: null,
    descriptors: [],
    limitedPortions: [],
    evidenceIds: [],
    evidenceExclusions: [],
    evidenceSuggestions: [],
    reportSentence: null,
    operationalTestPerformed: false,
    structuralConcern: false,
    severity: null,
    sortOrder,
    createdAt: now,
    updatedAt: now
  };
}

// ---------- transitions (pure) ----------

// Any status may move to any other status at any time — "completed" is never a lock
// (OWNER-CONTRACT §4.6, Frozen Spec §32). Leaving a reason-bearing status clears the reason.
export function withItemReportSentence(item: V2ChecklistItem, sentence: ItemReportSentence | null, now: number): V2ChecklistItem {
  return { ...item, reportSentence: sentence ? { ...sentence } : null, updatedAt: now };
}

export function withItemStatus(item: V2ChecklistItem, status: ItemStatus, now: number): V2ChecklistItem {
  return {
    ...item,
    status,
    statusReason: statusNeedsReason(status) ? item.statusReason : '',
    reasonCode: status === 'unableToInspect' ? item.reasonCode : null,
    // A whole-row Unable or an N/A has no partially observed portion (OD-5): the portions go with it.
    limitedPortions: canCarryLimitedPortions(status) ? item.limitedPortions : [],
    updatedAt: now
  };
}

export function withItemReason(item: V2ChecklistItem, reason: string, now: number): V2ChecklistItem {
  return { ...item, statusReason: reason.trim(), updatedAt: now };
}

// Unable to Inspect reason code (§15). Ignored unless the item is unableToInspect.
export function withItemReasonCode(item: V2ChecklistItem, reasonCode: UnableReason | null, now: number): V2ChecklistItem {
  if (item.status !== 'unableToInspect' || item.reasonCode === reasonCode) return item;
  return { ...item, reasonCode, updatedAt: now };
}

// Whether the inspector's result still lacks what the report needs: Unable to Inspect needs a
// reason, and — Owner decision OD-7 = C (NY F1 Round 3) — the reason `other` needs the inspector's
// own factual words (the note stays optional for every other closed code; `obstructed` prints the
// frozen template while the note is blank). Every other result is complete on its own.
export function itemResultIncomplete(item: Pick<V2ChecklistItem, 'status' | 'reasonCode'> & { statusReason?: string }): boolean {
  if (item.status !== 'unableToInspect') return false;
  if (item.reasonCode === null) return true;
  return unableReasonNeedsNote(item.reasonCode) && (item.statusReason ?? '').trim().length === 0;
}

// ---------- limited portions (NY F1 Round 3 · OD-5 = A) ----------

// Only a row that carries (or can still receive) an observed result has a partially observed portion.
export function canCarryLimitedPortions(status: ItemStatus): boolean {
  return status !== 'unableToInspect' && status !== 'notApplicable';
}

// A recorded portion whose reason is `other` still needs the inspector's words (OD-7 = C).
export function itemLimitationsIncomplete(item: Pick<V2ChecklistItem, 'limitedPortions'>): boolean {
  return item.limitedPortions.some(limitedPortionIncomplete);
}

function normalizedPortion(portion: LimitedPortion): LimitedPortion {
  return { reasonCode: portion.reasonCode, extent: portion.extent.trim(), note: portion.note.trim(), templateId: portion.templateId };
}

// Adds one limited portion to the row. The row's result, descriptors, evidence and notes are untouched;
// no row and no task is created. Ignored on a row that cannot carry portions or for `notInstalled`.
export function withLimitedPortion(item: V2ChecklistItem, portion: LimitedPortion, now: number): V2ChecklistItem {
  if (!canCarryLimitedPortions(item.status) || !isPortionReason(portion.reasonCode)) return item;
  return { ...item, limitedPortions: [...item.limitedPortions, normalizedPortion(portion)], updatedAt: now };
}

export function withLimitedPortionAt(item: V2ChecklistItem, index: number, portion: LimitedPortion, now: number): V2ChecklistItem {
  if (!canCarryLimitedPortions(item.status) || !isPortionReason(portion.reasonCode) || index < 0 || index >= item.limitedPortions.length) return item;
  return { ...item, limitedPortions: item.limitedPortions.map((p, i) => (i === index ? normalizedPortion(portion) : p)), updatedAt: now };
}

export function withoutLimitedPortion(item: V2ChecklistItem, index: number, now: number): V2ChecklistItem {
  if (index < 0 || index >= item.limitedPortions.length) return item;
  return { ...item, limitedPortions: item.limitedPortions.filter((_, i) => i !== index), updatedAt: now };
}

export function withItemNote(item: V2ChecklistItem, note: string, now: number): V2ChecklistItem {
  return { ...item, inspectorNote: note, updatedAt: now };
}

// NY F1 Round 2: one descriptor answer on one row. An empty value AND an empty detail remove the
// answer (nothing is ever stored as a blank), so "unanswered" stays distinguishable from every
// recorded value. Answers are kept in catalogue order so a record reads back deterministically.
const DESCRIPTOR_ORDER: ReadonlyMap<DescriptorKey, number> = new Map(DESCRIPTOR_CATALOGUE.map((d, i) => [d.key, i] as const));

export function withItemDescriptor(item: V2ChecklistItem, key: DescriptorKey, value: string, detail: string, now: number): V2ChecklistItem {
  const nextValue = value.trim();
  const nextDetail = detail.trim();
  const others = item.descriptors.filter((d) => d.key !== key);
  const next = nextValue.length === 0 && nextDetail.length === 0 ? others : [...others, { key, value: nextValue, detail: nextDetail }];
  next.sort((a, b) => (DESCRIPTOR_ORDER.get(a.key) ?? 0) - (DESCRIPTOR_ORDER.get(b.key) ?? 0));
  const same = next.length === item.descriptors.length && next.every((d, i) => d.key === item.descriptors[i].key && d.value === item.descriptors[i].value && d.detail === item.descriptors[i].detail);
  if (same) return item;
  return { ...item, descriptors: next, updatedAt: now };
}

// NY F1 Round 2: the property facts (#9 / #10 / #11), each written only when the inspector actually
// answered it; explicit null clears an answer back to "not asked". Nothing else on the record moves.
export interface PropertyFactsPatch {
  waterSupplySource?: UtilitySource | null;
  wasteDisposalSource?: UtilitySource | null;
  heatingFuel?: HeatingFuel | null;
}

export function withPropertyFacts(inspection: V2Inspection, patch: PropertyFactsPatch, now: number): V2Inspection {
  const property = { ...inspection.property };
  let changed = false;
  if (patch.waterSupplySource !== undefined && patch.waterSupplySource !== property.waterSupplySource) {
    property.waterSupplySource = patch.waterSupplySource;
    changed = true;
  }
  if (patch.wasteDisposalSource !== undefined && patch.wasteDisposalSource !== property.wasteDisposalSource) {
    property.wasteDisposalSource = patch.wasteDisposalSource;
    changed = true;
  }
  if (patch.heatingFuel !== undefined && patch.heatingFuel !== property.heatingFuel) {
    property.heatingFuel = patch.heatingFuel;
    changed = true;
  }
  if (!changed) return inspection;
  return { ...inspection, property, updatedAt: now };
}

// The INSPECTOR links a photo to the row (拍照 on the row · 相册 · 照片池): the link is made and any
// earlier removal of that photo is forgotten — the inspector's latest act is the override (CA-7).
export function withEvidenceLink(item: V2ChecklistItem, evidenceId: string, now: number): V2ChecklistItem {
  const excluded = item.evidenceExclusions.includes(evidenceId);
  const suggested = item.evidenceSuggestions.includes(evidenceId);
  if (item.evidenceIds.includes(evidenceId) && !excluded && !suggested) return item;
  return {
    ...item,
    evidenceIds: item.evidenceIds.includes(evidenceId) ? item.evidenceIds : [...item.evidenceIds, evidenceId],
    evidenceExclusions: excluded ? item.evidenceExclusions.filter((id) => id !== evidenceId) : item.evidenceExclusions,
    // Linking a proposed photo IS the confirmation: the proposal is settled.
    evidenceSuggestions: suggested ? item.evidenceSuggestions.filter((id) => id !== evidenceId) : item.evidenceSuggestions,
    updatedAt: now
  };
}

// The INSPECTOR removes a photo from the row: the link goes AND the removal is remembered, so nothing
// automatic ever puts the photo back on this row (CA-7 / §9.2).
export function withoutEvidenceLink(item: V2ChecklistItem, evidenceId: string, now: number): V2ChecklistItem {
  if (!item.evidenceIds.includes(evidenceId)) return item;
  return {
    ...item,
    evidenceIds: item.evidenceIds.filter((id) => id !== evidenceId),
    evidenceExclusions: item.evidenceExclusions.includes(evidenceId) ? item.evidenceExclusions : [...item.evidenceExclusions, evidenceId],
    evidenceSuggestions: item.evidenceSuggestions.filter((id) => id !== evidenceId),
    updatedAt: now
  };
}

// The AI places the photos its observation rests on (§9.1; 2026-10-27: a LINK marked 「AI 放的」 — the
// inspector's default is to trust the sort and only undo a wrong one). Automatic, so it respects every
// removal the inspector made: an excluded photo is skipped, silently and permanently; a photo already
// linked by hand keeps its hand-made link and gets no mark.
export function withEvidenceSuggestions(item: V2ChecklistItem, evidenceIds: readonly string[], now: number): V2ChecklistItem {
  const additions = [...new Set(evidenceIds.filter((id) => !item.evidenceIds.includes(id) && !item.evidenceExclusions.includes(id) && !item.evidenceSuggestions.includes(id)))];
  if (additions.length === 0) return item;
  return { ...item, evidenceIds: [...item.evidenceIds, ...additions], evidenceSuggestions: [...item.evidenceSuggestions, ...additions], updatedAt: now };
}

// The inspector says the AI-placed photo is NOT this row's: the link and the mark go and the removal is
// remembered like any other (CA-7) — no later run places it on this row again.
export function withEvidenceSuggestionDismissed(item: V2ChecklistItem, evidenceId: string, now: number): V2ChecklistItem {
  if (!item.evidenceSuggestions.includes(evidenceId)) return item;
  return {
    ...item,
    evidenceIds: item.evidenceIds.filter((id) => id !== evidenceId),
    evidenceSuggestions: item.evidenceSuggestions.filter((id) => id !== evidenceId),
    evidenceExclusions: item.evidenceExclusions.includes(evidenceId) ? item.evidenceExclusions : [...item.evidenceExclusions, evidenceId],
    updatedAt: now
  };
}

// A photo record deleted from the app: the link goes, and so does any override or proposal about it
// (nothing can link a photo that no longer exists).
export function withoutEvidenceEntirely(item: V2ChecklistItem, evidenceId: string, now: number): V2ChecklistItem {
  if (!item.evidenceIds.includes(evidenceId) && !item.evidenceExclusions.includes(evidenceId) && !item.evidenceSuggestions.includes(evidenceId)) return item;
  return {
    ...item,
    evidenceIds: item.evidenceIds.filter((id) => id !== evidenceId),
    evidenceExclusions: item.evidenceExclusions.filter((id) => id !== evidenceId),
    evidenceSuggestions: item.evidenceSuggestions.filter((id) => id !== evidenceId),
    updatedAt: now
  };
}

// 2026-09-13 ratification §4: the inspector confirms (or withdraws) the structural fact on the row.
export function withItemStructuralConcern(item: V2ChecklistItem, structuralConcern: boolean, now: number): V2ChecklistItem {
  if (item.structuralConcern === structuralConcern) return item;
  return { ...item, structuralConcern, updatedAt: now };
}

// ---------- progress ----------

export interface ProgressSummary {
  total: number;
  // Every row the inspector has said something about (any status but notChecked).
  settled: number;
  notChecked: number;
  ok: number;
  issues: number;
  notApplicable: number;
  unable: number;
}

export function progressFor(items: readonly V2ChecklistItem[]): ProgressSummary {
  const summary: ProgressSummary = {
    total: items.length,
    settled: 0,
    notChecked: 0,
    ok: 0,
    issues: 0,
    notApplicable: 0,
    unable: 0
  };
  for (const item of items) {
    switch (item.status) {
      case 'notChecked':
        summary.notChecked += 1;
        break;
      case 'checkedOk':
        summary.settled += 1;
        summary.ok += 1;
        break;
      case 'issueFound':
        summary.settled += 1;
        summary.issues += 1;
        break;
      case 'notApplicable':
        summary.settled += 1;
        summary.notApplicable += 1;
        break;
      case 'unableToInspect':
        summary.settled += 1;
        summary.unable += 1;
        break;
    }
  }
  return summary;
}

// Area completion is blocked only by rows that are genuinely unhandled (OWNER-CONTRACT §5.8).
// An empty area (inspector removed nothing — rows are never deleted — but a custom "other"
// area may start empty) can be completed once it has at least one settled row or a note.
export function areaCompletionBlockers(items: readonly V2ChecklistItem[]): V2ChecklistItem[] {
  return items.filter((i) => i.status === 'notChecked');
}

export function areaCanComplete(items: readonly V2ChecklistItem[]): boolean {
  return areaCompletionBlockers(items).length === 0;
}

export interface AreaCompletionSummary {
  enabledAreas: number;
  completedAreas: number;
}

export function areaCompletionFor(areas: readonly V2Area[]): AreaCompletionSummary {
  const enabled = areas.filter((a) => a.enabled);
  return {
    enabledAreas: enabled.length,
    completedAreas: enabled.filter((a) => a.completedAt !== null).length
  };
}

export function inspectionCanComplete(areas: readonly V2Area[]): boolean {
  const summary = areaCompletionFor(areas);
  return summary.enabledAreas > 0 && summary.completedAreas === summary.enabledAreas;
}

// The inspection status is derived from what the inspector actually did: any touch moves a
// draft to inProgress; completion is explicit and reversible.
export function touchedInspection(inspection: V2Inspection, now: number): V2Inspection {
  if (inspection.status === 'draft') return { ...inspection, status: 'inProgress', updatedAt: now };
  return { ...inspection, updatedAt: now };
}

export function reopenedInspection(inspection: V2Inspection, now: number): V2Inspection {
  if (inspection.status !== 'completed') return { ...inspection, updatedAt: now };
  return { ...inspection, status: 'inProgress', completedAt: null, updatedAt: now };
}

export function completedInspection(inspection: V2Inspection, now: number): V2Inspection {
  return { ...inspection, status: 'completed', completedAt: now, updatedAt: now };
}

// One-line address; `fallback` is what the UI shows for an inspection without an address (the
// UI passes a translated string — the domain carries no user-facing copy).
export function displayAddress(inspection: Pick<V2Inspection, 'property'>, fallback: string = '—'): string {
  const p = inspection.property;
  const line = [p.address, p.city, [p.state, p.zip].filter((s) => s.length > 0).join(' ')]
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(', ');
  return line.length > 0 ? line : fallback;
}
